import { useCallback, useId, useRef, useState, type KeyboardEvent, type ClipboardEvent } from 'react';
import { useFormField } from '../FormField';
import { Chip } from '../Chip';
import './MultiValueInput.css';

/**
 * MultiValueInput — 여러 값 입력 (디자인 정본 **B2-2**, 2026-09-05).
 *
 * 치고 **Enter** 를 누르면 칩이 된다. 목록에 없는 값을 여러 개 받아야 하는 자리 —
 * 자동분류 규칙의 메일주소가 첫 사용이다.
 *
 * ★★★ **B4 MultiSelect · B11 사람 찾기와 가르는 자리**
 *    · 고를 **목록이 있으면** B4 · **사람이면** B11 · **목록에 없는 글자면** 여기.
 *    바깥 메일주소는 어느 목록에도 없다 — 그래서 저 둘로는 못 한다.
 * ★★★ **칸을 떠날 때도 담는다.** 치다 만 값을 말없이 버리면 「분명히 적었는데 저장하니
 *    없다」가 된다 — 이 부류의 버그는 사용자가 원인을 절대 못 짚는다.
 * ★★ **모양이 틀리면 칩이 안 된다.** 그 자리에서 왜인지 말한다 — 담아 놓고 저장할 때
 *    거절하면 어느 줄이 문제인지가 사라진다.
 * ★  한 줄에 여러 개를 **붙여넣으면 갈라 담는다**(쉼표·세미콜론·줄바꿈·공백).
 *    메일주소는 다른 데서 복사해 온다 — 하나씩 다시 치게 하지 않는다.
 */

export interface MultiValueInputProps {
  values: string[];
  onChange: (next: string[]) => void;
  /** 담기 전에 씻는다 — 메일주소는 소문자로. 안 주면 앞뒤 공백만 없앤다. */
  normalize?: (raw: string) => string;
  /** 담을 수 있는 값인가. 아니면 그 자리에서 `invalidMessage` 를 적는다. */
  validate?: (value: string) => boolean;
  /** 모양이 틀렸을 때 적을 말. */
  invalidMessage?: string;
  /** 이미 있는 값일 때 적을 말. */
  duplicateMessage?: string;
  max?: number;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /** 「값 빼기」의 읽어 주는 이름 — `{v}` 자리에 값이 들어간다. */
  removeLabel?: string;
  className?: string;
}

const SPLIT = /[,;\n\t ]+/;

export function MultiValueInput({
  values,
  onChange,
  normalize = (raw) => raw.trim(),
  validate,
  invalidMessage = '담을 수 없는 값입니다',
  duplicateMessage = '이미 있습니다',
  max = 20,
  placeholder,
  disabled,
  ariaLabel,
  removeLabel = '{v} 빼기',
  className,
}: MultiValueInputProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const field = useFormField();
  const uid = useId();
  const errId = `${uid}-err`;

  /** 한 덩이를 갈라 담는다. 담긴 것이 있으면 true. */
  const commit = useCallback((raw: string): boolean => {
    const parts = raw.split(SPLIT).map((p) => normalize(p)).filter((p) => p !== '');
    if (parts.length === 0) return false;

    const next = [...values];
    for (const p of parts) {
      if (validate && !validate(p)) { setError(invalidMessage); return false; }
      if (next.includes(p)) { setError(duplicateMessage); return false; }
      if (next.length >= max) { setError(null); break; }
      next.push(p);
    }
    setError(null);
    if (next.length !== values.length) onChange(next);
    return true;
  }, [values, onChange, normalize, validate, invalidMessage, duplicateMessage, max]);

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      // ★ 폼이 통째로 올라가는 것을 막는다 — 팝업 안에서 Enter 는 「담기」다.
      e.preventDefault();
      if (commit(draft)) setDraft('');
      return;
    }
    if (e.key === 'Backspace' && draft === '' && values.length > 0) {
      e.preventDefault();
      onChange(values.slice(0, -1));
      setError(null);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (!SPLIT.test(text)) return;
    e.preventDefault();
    if (commit(text)) setDraft('');
  };

  const remove = (v: string) => {
    onChange(values.filter((x) => x !== v));
    setError(null);
  };

  const full = values.length >= max;

  return (
    <div className={`wc-mvi__wrap${className ? ` ${className}` : ''}`}>
      <div
        className={`wc-mvi${error ? ' is-invalid' : ''}${disabled ? ' is-disabled' : ''}`}
        onClick={() => inputRef.current?.focus()}
      >
        {/*
          정본 D14 Chip (2026-09-07) — 종전에는 이 컴포넌트가 **제 칩을 손으로 짰다**.
          「정본에 없으면 다음 사람은 또 손으로 짠다」가 정본 안에서 일어난 자리였다.
          ★ 칩과 `<input>` 이 한 줄에 섞여 흐르므로 `inline`(= `<span role="listitem">`).
        */}
        {values.map((v) => (
          <Chip
            key={v}
            inline
            onRemove={() => remove(v)}
            removeLabel={removeLabel.replace('{v}', v)}
            removeDisabled={disabled}
            onClick={(e) => e.stopPropagation()}
          >
            {v}
          </Chip>
        ))}
        <input
          ref={inputRef}
          className="wc-mvi__input"
          value={draft}
          disabled={disabled || full}
          placeholder={values.length === 0 || !full ? placeholder : undefined}
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errId : field?.describedBy}
          onChange={(e) => { setDraft(e.target.value); if (error) setError(null); }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          /* ★ 칸을 떠날 때도 담는다 — 치다 만 값을 버리지 않는다(정본 B2-2). */
          onBlur={() => { if (commit(draft)) setDraft(''); }}
        />
      </div>
      <p className={`wc-mvi__note${error ? ' is-bad' : ''}`} id={error ? errId : undefined} role={error ? 'alert' : undefined}>
        {error ?? `${values.length} / ${max}`}
      </p>
    </div>
  );
}

export default MultiValueInput;
